import Toggle from "app/components/Toggle";
import { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectCompanySettings,
  selectUpdateVisibilitySettingsStatus,
  updateCompanySettings,
  updateCompanySettingsAction,
  updateVisibilitySettings,
} from "./slice";
import { useDebounceCallback } from "utils/hooks";
import VisibilitySettings from "./Cards/VisibilitySettings";

const TeamScanAdminConsoleSettings = () => {
  const dispatch = useAppDispatch();
  // ------------------------ Selectors ------------------------------------
  const companySettings = useAppSelector(selectCompanySettings);
  const updatingVisibilityDefaultsStatus = useAppSelector(
    selectUpdateVisibilitySettingsStatus
  );

  // ------------------------ States ------------------------------------
  const [visibilityOverrideToggle, setVisibilityOverrideToggle] =
    useState(false);
  const [visibilitySetting, setVisibilitySetting] = useState(1);
  // ------------------------ Use Effects ------------------------------------
  useEffect(() => {
    setVisibilityOverrideToggle(
      !!companySettings?.allowVisibilityPermissionOverride
    );
  }, [companySettings?.allowVisibilityPermissionOverride]);

  useEffect(() => {
    setVisibilitySetting(companySettings?.visibilityPermissionDefaultId || 1);
  }, [companySettings?.visibilityPermissionDefaultId]);

  // ------------------------ Memoized Constants ------------------------------------
  const companyAccountId = useMemo(
    () => companySettings?.companyAccountId ?? null,
    [companySettings?.companyAccountId]
  );

  // ------------------------ Debounce Functions ------------------------------------
  const onSaveSuggestedConversationsSetting = useDebounceCallback(
    (value: boolean) => {
      if (!companyAccountId) {
        return;
      }

      dispatch(
        updateCompanySettings({
          payload: { hasSuggestedConversations: Number(value) as 0 | 1 },
          companyAccountId: companyAccountId,
        })
      );
    },
    1000
  );

  const onVisibilityOverrideToggle = useDebounceCallback((value: boolean) => {
    if (!companyAccountId) {
      return;
    }

    dispatch(
      updateVisibilitySettings({
        payload: { allowVisibilityPermissionOverride: Number(value) as 0 | 1 },
      })
    );
  }, 1000);

  return (
    <Card className="admin-console-settings-card">
      <div>
        <h2 id="visibility-settings">TEAMscan</h2>
        <p>Settings related to the TEAMscan teamwork survey.</p>
      </div>
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Enable Suggested Conversations</h3>
          <p>
            Allow team members to view suggested conversations with other
            members in the organization based on lowest and highest scoring
            teamwork areas.
          </p>
        </div>
        <div className="setting-row-action">
          <Toggle
            isOn={companySettings?.hasSuggestedConversations === 1}
            handleToggle={() => {
              const updatedValue =
                companySettings?.hasSuggestedConversations === 1 ? 0 : 1;
              dispatch(
                updateCompanySettingsAction({
                  hasSuggestedConversations: updatedValue,
                })
              );
              onSaveSuggestedConversationsSetting(!!updatedValue);
            }}
          />
        </div>
      </div>
      <hr className="m-0" />
      <h3 className="admin-console-settings-card-subheader">TEAMscan Survey</h3>
      <VisibilitySettings
        visibilitySetting={visibilitySetting}
        visibilityOverrideToggle={visibilityOverrideToggle}
        setVisibilityOverrideToggle={(payload) => {
          onVisibilityOverrideToggle(payload);
          setVisibilityOverrideToggle(payload);
        }}
        onSaveVisibilityDefaults={async ({
          reconfigureVisibilityPermissions,
          visibilityPermissionDefaultId,
        }) => {
          if (!companyAccountId) {
            return;
          }
          await dispatch(
            updateVisibilitySettings({
              payload: {
                reconfigureVisibilityPermissions,
                visibilityPermissionDefaultId,
              },
            })
          );
        }}
        isChangingDefaultsLoading={
          updatingVisibilityDefaultsStatus === "loading"
        }
      />
    </Card>
  );
};

export default TeamScanAdminConsoleSettings;
